export function initForAlpine(alpine) {
  alpine.directive("submit-on-change", setupSubmitOnChange);
}

const MODIFIER_INVERTED = "inverted";

/**
 * Submits an AJAX request when the element's `change` event is fired.
 * **This only supports the `checked` property of the element.**
 *
 * The element's `disabled` property is set to `true` while the request is
 * in flight.
 *
 * Available modifiers:
 * - `inverted` inverts the value before submitting it.
 *
 * This Alpine directive is intended to replace the same class-based approach
 * (`.js-submit-on-change`, see [assets/app_js/framework/submit_on_change.js](../framework/submit_on_change.js))
 * with a more explicit usage.
 *
 * ## Example
 * ### Default method (post)
 *
 *     <input name="myInput" x-submit-on-change="/target-url" />
 *
 * Sends the following HTTP request:
 *
 *     POST /target-url
 *     Content-Type: application/x-www-form-urlencoded; charset=UTF-8
 *
 *     myInput=true
 *
 * ### Specific method
 *
 *     <input name="myInput" x-submit-on-change:delete="/target-url" />
 *
 * Sends the following HTTP request:
 *
 *     DELETE /target-url
 *     Content-Type: application/x-www-form-urlencoded; charset=UTF-8
 *
 *     myInput=true
 *
 * ### Invert the value before sending
 *
 *     <input name="myInput" x-submit-on-change:post.inverted="/target-url" />
 *
 * Assuming `element.checked === false`, sends the following HTTP request:
 *
 *     POST /target-url
 *     Content-Type: application/x-www-form-urlencoded; charset=UTF-8
 *
 *     myInput=true
 */
function setupSubmitOnChange(element, { value, expression, modifiers }, { evaluateLater, effect, cleanup }) {
  if (!expression) {
    console.error("[x-submit-on-change] missing target URL expression", element);
    return;
  }

  const method = value ?? "post";
  const getUrlEvaluatorFn = evaluateLater(expression);
  const grab = { handler: null };

  effect(() => {
    getUrlEvaluatorFn((url) => {
      element.removeEventListener("change", grab.handler);
      grab.handler = createHandler(method, url, modifiers);
      element.addEventListener("change", grab.handler);
    });
  });

  cleanup(() => {
    element.removeEventListener("change", grab.handler);
  });
}

function createHandler(method, url, modifiers) {
  return function (event) {
    event.preventDefault();
    event.stopPropagation();
    const isInverted = modifiers.includes(MODIFIER_INVERTED);

    const data = {
      [event.target.getAttribute("name")]: isInverted ? !event.target.checked : event.target.checked,
    };

    event.target.disabled = true;

    ajax({
      url,
      method,
      data,
      dataType: "json",
      error: function (_, status, error) {
        console.error(`[x-submit-on-change] error in submit-on-change directive (${method} ${url})`, { status, error });
      },
      success: function (data) {
        if (data.success) {
          document.dispatchEvent(new CustomEvent("recompute_workflow"));
        } else {
          location.reload();
        }
      },
      complete: function () {
        event.target.disabled = false;
      },
    });
  };
}
